/**
 * ScheduledTimesDisplay — 022-checkin-checkout-times
 *
 * Renders the scheduled check-in and check-out times for a booking in a
 * compact two-column layout. Used in the booking detail panel / expanded row.
 *
 * Behaviour:
 * - If a time is absent (legacy booking), shows a date-only fallback.
 * - Always appends "(UTC)" next to each time value per the feature spec.
 * - If originalCheckOutTime differs from scheduledCheckOutTime, shows a
 *   "deviation" note indicating the check-out was extended.
 * - If actual timestamps (checkedInAt / checkedOutAt) are present, shows them
 *   below the scheduled times.
 */

import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Booking } from '../types/booking';
import { formatDate } from '../utils/formatDate';

interface ScheduledTimesDisplayProps {
  booking: Booking;
}

/** Formats a UTC ISO timestamp as a date+time string for display. */
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const date = d.toISOString().split('T')[0];
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${date} ${hh}:${mm}`;
}

export const ScheduledTimesDisplay = ({ booking }: ScheduledTimesDisplayProps) => {
  const { t } = useTranslation(['pages']);

  const {
    checkInDate,
    checkOutDate,
    scheduledCheckInTime,
    scheduledCheckOutTime,
    originalCheckOutTime,
    checkedInAt,
    checkedOutAt,
  } = booking;

  const scheduledCheckInLabel = scheduledCheckInTime
    ? `${formatDate(checkInDate)} ${scheduledCheckInTime} ${t('pages:utcLabel')}`
    : formatDate(checkInDate);

  const scheduledCheckOutLabel = scheduledCheckOutTime
    ? `${formatDate(checkOutDate)} ${scheduledCheckOutTime} ${t('pages:utcLabel')}`
    : formatDate(checkOutDate);

  // Is the current check-out time later than the original?
  const hasDeviation =
    originalCheckOutTime &&
    scheduledCheckOutTime &&
    originalCheckOutTime !== scheduledCheckOutTime;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
      {/* Scheduled check-in */}
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
          {t('pages:scheduledCheckIn')}
        </p>
        <p className="flex items-center gap-1.5 text-gray-800">
          <Clock size={14} className="text-gray-400 shrink-0" />
          {scheduledCheckInLabel}
        </p>
        {checkedInAt && (
          <p className="mt-1 text-xs text-gray-500">
            {t('pages:actualCheckIn')}: {formatTimestamp(checkedInAt)}{' '}
            {t('pages:utcLabel')}
          </p>
        )}
      </div>

      {/* Scheduled check-out */}
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
          {t('pages:scheduledCheckOut')}
        </p>
        <p className="flex items-center gap-1.5 text-gray-800">
          <Clock size={14} className="text-gray-400 shrink-0" />
          {scheduledCheckOutLabel}
        </p>
        {hasDeviation && originalCheckOutTime && (
          <p className="mt-1 text-xs text-amber-700">
            {t('pages:originalCheckOut')}:{' '}
            {formatDate(checkOutDate)} {originalCheckOutTime}{' '}
            {t('pages:utcLabel')}
          </p>
        )}
        {checkedOutAt && (
          <p className="mt-1 text-xs text-gray-500">
            {t('pages:actualCheckOut')}: {formatTimestamp(checkedOutAt)}{' '}
            {t('pages:utcLabel')}
          </p>
        )}
      </div>
    </div>
  );
};
